'use client'

/**
 * Client-side logger that wraps console methods.
 * Provides consistent API matching the server logger.
 *
 * In production, only warn and error levels are logged.
 * In development, all levels are logged.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface ClientLogContext {
  module?: string
  [key: string]: unknown
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

const MIN_LOG_LEVEL: LogLevel = process.env.NODE_ENV === 'production' ? 'warn' : 'debug'

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[MIN_LOG_LEVEL]
}

function formatMessage(module: string | undefined, msg: string): string {
  return module ? `[${module}] ${msg}` : msg
}

class ClientLogger {
  private context: ClientLogContext

  constructor(context: ClientLogContext = {}) {
    this.context = context
  }

  /**
   * Creates a child logger with additional context.
   */
  child(bindings: ClientLogContext): ClientLogger {
    return new ClientLogger({ ...this.context, ...bindings })
  }

  /**
   * Debug level - diagnostic information for development.
   * Matches Pino API: (obj, msg) or (msg)
   */
  debug(objOrMsg: Record<string, unknown> | string, msg?: string): void {
    if (!shouldLog('debug')) return
    if (typeof objOrMsg === 'string') {
      console.debug(formatMessage(this.context.module, objOrMsg))
    } else {
      console.debug(formatMessage(this.context.module, msg || ''), objOrMsg)
    }
  }

  /**
   * Info level - normal operational messages.
   * Matches Pino API: (obj, msg) or (msg)
   */
  info(objOrMsg: Record<string, unknown> | string, msg?: string): void {
    if (!shouldLog('info')) return
    if (typeof objOrMsg === 'string') {
      console.info(formatMessage(this.context.module, objOrMsg))
    } else {
      console.info(formatMessage(this.context.module, msg || ''), objOrMsg)
    }
  }

  /**
   * Warn level - unexpected but handled situations.
   * Matches Pino API: (obj, msg) or (msg)
   */
  warn(objOrMsg: Record<string, unknown> | string, msg?: string): void {
    if (!shouldLog('warn')) return
    if (typeof objOrMsg === 'string') {
      console.warn(formatMessage(this.context.module, objOrMsg))
    } else {
      console.warn(formatMessage(this.context.module, msg || ''), objOrMsg)
    }
  }

  /**
   * Error level - failures requiring attention.
   * Matches Pino API: (obj, msg) or (msg)
   */
  error(objOrMsg: Record<string, unknown> | string, msg?: string): void {
    if (!shouldLog('error')) return
    if (typeof objOrMsg === 'string') {
      console.error(formatMessage(this.context.module, objOrMsg))
    } else {
      console.error(formatMessage(this.context.module, msg || ''), objOrMsg)
    }
  }
}

/**
 * Creates a client-side logger with module context.
 * API matches Pino for consistency between client and server logging.
 *
 * @example
 * const log = createClientLogger('CartStore')
 * log.info({ productId, quantity }, 'Item added to cart')
 * log.error({ error }, 'Failed to add item')
 */
export function createClientLogger(module: string): ClientLogger {
  return new ClientLogger({ module })
}

export const clientLogger = new ClientLogger()
export default clientLogger
